'use client'

import { usePathname } from 'next/navigation'

import { BackToHomeButton } from '@/app/components/BackToHomeButton'
import { PageBuilder } from '@/app/components/PageBuilder'
import { PageHeading } from '@/app/components/PageHeading'
import { useUser } from '@/app/contexts/UserContext'
import { BLOG_INDEX_PATH, isBlogPath } from '@/lib/isBlogPath'
import { GetPageQueryResult } from '@/sanity.types'

type ErrorPageContentProps = {
  page: GetPageQueryResult | null
  fallbackTitle?: string
  fallbackMessage?: string
}

function getAccountHomePath(userType: string) {
  return userType === 'publisher' ? '/publisher/campaigns' : '/advertiser/campaigns'
}

export function ErrorPageContent({
  page,
  fallbackTitle = 'We checked the spam folder',
  fallbackMessage = "Still couldn't find it.",
}: ErrorPageContentProps) {
  const { user, isLoading } = useUser()
  const isBlog = isBlogPath(usePathname())
  const isAuthenticated = user?.user_type === 'advertiser' || user?.user_type === 'publisher'
  const hasContent = page?._id && page.pageBuilder && page.pageBuilder.length > 0

  if (!hasContent) {
    return (
      <div className="content-container mt-10xl mb-12xl lg:mt-12xl lg:mb-13xl">
        <div className="max-w-[1144px] mx-auto">
          <div className="max-w-[564px] ml-0 flex flex-col gap-xl">
            <div className="flex flex-col">
              <p className="size-display-d1 text-white-950 -mb-lg">404</p>
              <p className="size-header-h3 font-normal text-white-950 m-0">Page not found</p>
            </div>
            <h1 className="!size-header-h3 !font-normal text-white-950 lg:!size-header-h4 lg:!font-normal">
              {fallbackTitle}
            </h1>
            <p className="size-body-md text-white-900">{fallbackMessage}</p>
            <div className="flex flex-wrap gap-md mt-xl">
              {isLoading ? (
                // Placeholder at button height so the resolved action doesn't shift layout
                <div className="h-[56px]" suppressHydrationWarning />
              ) : isAuthenticated ? (
                <>
                  <a
                    className="btn-primary no-underline hover:no-underline"
                    href="https://help.paved.com"
                  >
                    Knowledge base
                  </a>
                  <a
                    className="btn-secondary no-underline hover:no-underline"
                    href={getAccountHomePath(user.user_type)}
                  >
                    Account home
                  </a>
                </>
              ) : (
                // Blog 404s only differ here: the way back leads to the blog index
                <BackToHomeButton
                  {...(isBlog && {
                    href: BLOG_INDEX_PATH,
                    label: 'Back to blog home',
                    dataId: 'backToBlog',
                  })}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <PageHeading page={page} />
      <PageBuilder page={page} />
    </>
  )
}
